import * as React from "react";
import { Types } from "@grandvisionhq/graphql";
import { useToast } from "@grandvisionhq/iris-ui";
import { useIntl } from "@grandvisionhq/state";
import { useInitiatePaypalTransaction } from "./use-initiate-paypal-transaction.js";
import { usePaypalClientConfiguration } from "../../../../context/Paypal/context/Paypal/PaypalClientConfigurationContext.js";
const useCreatePaypalPaymentEvents = ({
  cartId,
  onPaymentAuthorization,
  onPaymentTokenReceive,
  hasSubscription,
  checkoutType,
  onClick
}) => {
  const { toast } = useToast();
  const { getLabel } = useIntl();
  const [initiatePaypalTransaction] = useInitiatePaypalTransaction({
    cartId,
    checkoutType: checkoutType || Types.CheckoutType.REGULAR
  });
  const paypalConfig = usePaypalClientConfiguration();
  const createOrder = React.useCallback(async () => {
    const { data, errors } = await initiatePaypalTransaction();
    const response = data?.initiatePaypalTransaction.response;
    if (errors || !response) {
      throw new Error("Could not initiate paypal transaction");
    }
    if (response.__typename !== "PaypalOrderResponse") {
      throw new Error("Subscription not implemented for create order");
    }
    if (onPaymentTokenReceive) {
      if (response.token) {
        onPaymentTokenReceive(response.token);
      } else {
        throw new Error("Did not receive payment token");
      }
    }
    return response.id;
  }, [initiatePaypalTransaction, onPaymentTokenReceive]);
  const createBillingAgreement = React.useCallback(async () => {
    const { data, errors } = await initiatePaypalTransaction();
    const response = data?.initiatePaypalTransaction.response;
    if (errors || !response) {
      throw new Error("Could not initiate paypal transaction");
    }
    if (response.__typename !== "PaypalSubscriptionResponse") {
      throw new Error("Billing agreement not supported for regular orders");
    }
    return response.billingAgreementTokenId;
  }, [initiatePaypalTransaction]);
  const onApprove = React.useCallback(
    // Disabling the rule because the onApprove type from react-paypal-js requires a promise
    // eslint-disable-next-line @typescript-eslint/require-await
    async (data, actions) => {
      try {
        if (!actions.order || hasSubscription) {
          onPaymentAuthorization(data.billingToken);
        } else {
          const details = await actions.order.get();
          const shippingAddress = details.purchase_units[0]?.shipping?.address;
          const name = details.purchase_units[0]?.shipping?.name;
          const { countryCode, postalCode, fullName, streetName, hasValidTextLength } = paypalConfig?.addressValidation || {};
          let isValidAddress = true;
          if (!countryCode?.(shippingAddress?.country_code || "") || !postalCode?.(shippingAddress?.postal_code || "")) {
            toast.error(getLabel("checkout.payment.paypal.invalidCountry"));
            isValidAddress = false;
          }
          if (checkoutType === Types.CheckoutType.EXPRESS) {
            if (streetName && !streetName(shippingAddress?.address_line_1 || "")) {
              toast.error(getLabel("checkout.payment.paypal.street.error.invalid.characters"));
              isValidAddress = false;
            }
            if (fullName && !fullName(name?.full_name || "")) {
              toast.error(getLabel("checkout.payment.paypal.street.error.fullname.characters"));
              isValidAddress = false;
            }
            if (hasValidTextLength && !hasValidTextLength(shippingAddress?.address_line_1 || "", "address_line_1")) {
              toast.error(getLabel("checkout.payment.paypal.street.error.invalid.length"));
              isValidAddress = false;
            }
            if (hasValidTextLength && !hasValidTextLength(name?.full_name || "", "fullName")) {
              toast.error(getLabel("checkout.payment.paypal.fullname.error.invalid.length"));
              isValidAddress = false;
            }
          }
          if (isValidAddress) {
            onPaymentAuthorization(data.orderID);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    [
      hasSubscription,
      onPaymentAuthorization,
      paypalConfig?.addressValidation,
      toast,
      getLabel,
      checkoutType
    ]
  );
  return {
    onError: console.error,
    onApprove,
    createOrder: !hasSubscription ? createOrder : void 0,
    createBillingAgreement: hasSubscription ? createBillingAgreement : void 0,
    onClick
  };
};
export {
  useCreatePaypalPaymentEvents
};
//# sourceMappingURL=use-create-paypal-payment-events.js.map
